import { UserRole, ADMIN_PORTAL_ROLES, moneyScope, seesPerformanceMoney } from '@jalupro/shared';
import { useAuth } from './AuthContext';
import { ADMIN_NAV, AGENT_NAV, firstNavPath } from '../components/nav';

/**
 * Financial visibility, layer 2 (Area 1 §5): for a role without financial visibility
 * the money columns, tiles and chart axes do not exist in the UI — the server has
 * already removed the fields (layer 1); this hook keeps the markup from rendering
 * empty cells. Super Admin / Accountant see everything; the Agent sees own figures.
 */
export function useMoneyVisible(): boolean {
  const { role } = useAuth();
  return !!role && moneyScope(role) !== 'NONE';
}

/**
 * Role grouping helpers. The admin portal (/admin) is shared by every staff role
 * (everyone but the Agent), filtered inside by the permission matrix; the AGENT has
 * their own portal (/agent).
 */

/** Roles that may enter the admin portal at /admin. */
export const ADMIN_AREA_ROLES: readonly UserRole[] = ADMIN_PORTAL_ROLES;

export function isAdminArea(role: UserRole): boolean {
  return ADMIN_AREA_ROLES.includes(role);
}

export function isAgent(role: UserRole): boolean {
  return role === UserRole.AGENT;
}

/**
 * Where to land a freshly-authenticated user based on role. Staff without the admin
 * dashboard (the Warehouse, until the pick-list screen exists) land on the catalog.
 */
export function homePathForRole(role: UserRole): string {
  if (isAgent(role)) return firstNavPath(AGENT_NAV, role, '/agent/settings');
  // The dashboard item points at /admin itself; otherwise the first visible module.
  return firstNavPath(ADMIN_NAV, role, '/admin/settings');
}

/**
 * Sales & performance money (revenue per agent, lifetime value, euro KPI tiles) is the
 * Super Admins' view alone — the Accountant sees units there (Area 1 §5). The Agent
 * sees their own figures.
 */
export function usePerformanceMoneyVisible(): boolean {
  const { role } = useAuth();
  return !!role && seesPerformanceMoney(role);
}
